// src/host/HostBookings.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/useAuth";
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, createOrGetInquiryThread } from "../firebase";
import { bookingApprove, bookingDecline } from "../lib/stripeRpc"; // ✅ same-origin RPC

/* ------------------------------ Utils ------------------------------ */
const fmtUSD = (cents) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
  }).format(Math.round(Number(cents || 0)) / 100);

const parseYMD = (s) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(s || ""))) return null;
  const [y, m, d] = s.split("-").map((n) => Number(n));
  return new Date(y, m - 1, d);
};

const dayFmt = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function describeSpan({ mode, startDate, endDateExclusive }) {
  if (mode === "overnight") {
    const start = parseYMD(startDate);
    const endEx = parseYMD(endDateExclusive);
    if (!start || !endEx) return "—";
    const endInclusive = new Date(endEx);
    endInclusive.setDate(endInclusive.getDate() - 1);
    if (start.toDateString() === endInclusive.toDateString()) {
      return dayFmt.format(start);
    }
    if (
      start.getFullYear() === endInclusive.getFullYear() &&
      start.getMonth() === endInclusive.getMonth()
    ) {
      return `${new Intl.DateTimeFormat(undefined, {
        month: "short",
      }).format(start)} ${start.getDate()}–${endInclusive.getDate()}, ${start.getFullYear()}`;
    }
    return `${dayFmt.format(start)} – ${dayFmt.format(endInclusive)}`;
  }
  return startDate ? dayFmt.format(parseYMD(startDate)) : "—";
}

function fmtCountdown(ms) {
  if (ms <= 0) return "Expired";
  const s = Math.floor(ms / 1000);
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

/** Robust ms parser: Firestore Timestamp, {seconds,nanoseconds}, number (sec/ms), ISO */
const toMs = (v) => {
  if (!v) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v === "object" && typeof v.seconds === "number") {
    return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
  }
  if (typeof v === "number") return v > 1e12 ? v : v * 1000; // sec → ms
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : 0;
};

const isPendingish = (status) => {
  const s = String(status ?? "").toLowerCase().trim();
  return (
    s === "" ||
    s === "pending" ||
    s === "open" ||
    s === "awaiting" ||
    s === "awaiting_host" ||
    s === "hold" ||
    s === "held" ||
    s === "payment_pending" ||
    s === "authorized" // manual-capture auth
  );
};

const pickExpiresField = (o) =>
  o?.approvalExpiresAt ??
  o?.approvalExpireAt ??
  o?.expiresAt ??
  o?.expireAt ??
  o?.deadline ??
  o?.deadlineAt ??
  o?.acceptBy ??
  o?.acceptUntil ??
  o?.expires ??
  o?.expiresOn ??
  o?.hold?.expiresAt ??
  o?.hold?.until ??
  o?.meta?.expiresAt ??
  null;

/**
 * Derive expiration with sensible defaults.
 * - For approvals: default to 4h if missing explicit field.
 * - For legacy bookingRequests: default to 24h.
 */
const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const deriveExpiresMs = (item, kind /* 'booking' | 'request' */) => {
  const explicit = pickExpiresField(item);
  const ms = toMs(explicit);
  if (ms) return ms;

  const createdMs = toMs(item?.createdAt);
  if (!createdMs) return 0;

  // TTL variants
  if (typeof item?.ttlMs === "number") return createdMs + item.ttlMs;
  if (typeof item?.ttlSeconds === "number")
    return createdMs + item.ttlSeconds * 1000;
  if (typeof item?.ttlHours === "number")
    return createdMs + item.ttlHours * 3600 * 1000;
  if (typeof item?.holdTTLHours === "number")
    return createdMs + item.holdTTLHours * 3600 * 1000;

  // Only default if it's actually pending-ish / awaiting approval
  const approvalPending =
    String(item?.approvalStatus || "").toLowerCase() === "pending" ||
    String(item?.status || "").toLowerCase() === "authorized" ||
    String(item?.status || "").toLowerCase() === "awaiting_host";

  if (kind === "booking") return approvalPending ? createdMs + FOUR_HOURS_MS : 0;
  return isPendingish(item?.status) ? createdMs + ONE_DAY_MS : 0;
};

/* ------------------------------ Page ------------------------------ */
export default function HostBookings() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // live second tick (countdowns)
  const [now, setNow] = useState(Date.now());

  // data
  const [bookings, setBookings] = useState([]);
  const [requestsRaw, setRequestsRaw] = useState([]);

  // caches
  const [listingsById, setListingsById] = useState({});
  const [guestsById, setGuestsById] = useState({});

  // ui
  const [msg, setMsg] = useState("");
  const [actingId, setActingId] = useState(null);

  // Legacy callables for the *requests* queue (gracefully handle if not deployed)
  const functions = getFunctions(undefined, "us-central1");
  const callAccept = useMemo(() => {
    try {
      return httpsCallable(functions, "acceptBooking");
    } catch {
      return null;
    }
  }, [functions]);
  const callDeny = useMemo(() => {
    try {
      return httpsCallable(functions, "denyBooking");
    } catch {
      return null;
    }
  }, [functions]);

  // ✅ Host cancel callable (canonical bookings)
  const callHostCancel = useMemo(() => {
    try {
      return httpsCallable(functions, "hostCancelBooking");
    } catch {
      return null;
    }
  }, [functions]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  /* ---- BOOKINGS (confirmed + pending/holds) ---- */
  useEffect(() => {
    if (loading || !user) return;
    const qRef = query(
      collection(db, "bookings"),
      where("hostUid", "==", user.uid),
      where("mode", "in", ["day", "overnight"]),
      orderBy("createdAt", "desc"),
      limit(200)
    );
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const out = [];
        snap.forEach((d) => out.push({ id: d.id, ...(d.data() || {}) }));
        setBookings(out);
      },
      (err) => {
        console.error(err);
        setMsg("Couldn’t load bookings.");
      }
    );
    return () => unsub();
  }, [loading, user]);

  /* ---- REQUESTS (legacy pipeline) ---- */
  useEffect(() => {
    if (loading || !user) return;
    const qRef = query(
      collection(db, "bookingRequests"),
      where("hostUid", "==", user.uid),
      orderBy("createdAt", "desc"),
      limit(150)
    );
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const out = [];
        snap.forEach((d) => out.push({ id: d.id, ...(d.data() || {}) }));
        setRequestsRaw(out);
      },
      (err) => {
        console.error(err);
        setMsg("Couldn’t load requests.");
      }
    );
    return () => unsub();
  }, [loading, user]);

  /* ---- Join lookups ---- */
  useEffect(() => {
    const missingListings = new Set();
    const missingGuests = new Set();
    for (const r of [...bookings, ...requestsRaw]) {
      if (r?.listingId && !listingsById[r.listingId])
        missingListings.add(r.listingId);
      if (r?.guestUid && !guestsById[r.guestUid])
        missingGuests.add(r.guestUid);
    }
    if (!missingListings.size && !missingGuests.size) return;

    let cancelled = false;
    (async () => {
      const listingUpdates = {};
      for (const lid of missingListings) {
        try {
          const snap = await getDoc(doc(db, "listings", String(lid)));
          if (snap.exists())
            listingUpdates[lid] = { id: snap.id, ...(snap.data() || {}) };
        } catch (e) {
          console.error("fetch listing failed", lid, e);
        }
      }
      if (!cancelled && Object.keys(listingUpdates).length) {
        setListingsById((m) => ({ ...m, ...listingUpdates }));
      }

      const guestUpdates = {};
      for (const uid of missingGuests) {
        try {
          const snap = await getDoc(doc(db, "users", String(uid)));
          if (snap.exists())
            guestUpdates[uid] = { uid: snap.id, ...(snap.data() || {}) };
        } catch (e) {
          console.error("fetch user failed", uid, e);
        }
      }
      if (!cancelled && Object.keys(guestUpdates).length) {
        setGuestsById((m) => ({ ...m, ...guestUpdates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookings, requestsRaw, listingsById, guestsById]);

  /* ---- Derivatives ---- */
  const requests = useMemo(() => {
    const pending = requestsRaw.filter((r) => isPendingish(r.status));
    return pending.sort((a, b) => {
      const ea = deriveExpiresMs(a, "request");
      const eb = deriveExpiresMs(b, "request");
      return (ea || Infinity) - (eb || Infinity);
    });
  }, [requestsRaw]);

  const emptyBookings = !loading && user && bookings.length === 0;
  const pendingCount = requests.length;

  const counts = useMemo(() => {
    const c = { paid: 0, pending: 0, canceled: 0, refunded: 0 };
    for (const r of bookings) {
      const s = String(r.status || "").toLowerCase();
      if (s in c) c[s] += 1;
    }
    return c;
  }, [bookings]);

  /* ---- Actions ---- */
  const contactGuest = async (item, opts = {}) => {
    try {
      if (!user) return;
      const listing = listingsById[item.listingId] || {};
      const thread = await createOrGetInquiryThread({
        listingId: item.listingId,
        hostUid: item.hostUid || user.uid,
        guestUid: item.guestUid,
        bookingId: item.id,
        listingTitle: listing?.title,
        listingPhoto: Array.isArray(listing?.photos)
          ? listing.photos[0]
          : undefined,
        ...opts,
      });
      navigate(`/messages?thread=${encodeURIComponent(thread.id)}`);
    } catch (e) {
      console.error(e);
      setMsg("Couldn’t open the conversation. Please try again.");
    }
  };

  // Legacy request handlers (callables)
  const handleAccept = async (reqId) => {
    try {
      if (!callAccept)
        throw new Error("Accept action unavailable (function not deployed).");
      setActingId(reqId);
      setRequestsRaw((arr) =>
        arr.map((r) =>
          r.id === reqId
            ? { ...r, status: "approved", approvalStatus: "approved" }
            : r
        )
      );
      const res = await callAccept({ id: reqId });
      if (!res?.data?.ok)
        setMsg("Accept failed. It may have expired or changed.");
      else setMsg("Request accepted.");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Accept failed.");
    } finally {
      setActingId(null);
    }
  };

  const handleDeny = async (reqId) => {
    try {
      if (
        !window.confirm("Deny this request? The guest will be notified.")
      )
        return;
      if (!callDeny)
        throw new Error("Deny action unavailable (function not deployed).");
      setActingId(reqId);
      setRequestsRaw((arr) =>
        arr.map((r) =>
          r.id === reqId
            ? { ...r, status: "denied", approvalStatus: "denied" }
            : r
        )
      );
      const res = await callDeny({ id: reqId });
      if (!res?.data?.ok)
        setMsg("Deny failed. It may have expired or changed.");
      else setMsg("Request denied.");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Deny failed.");
    } finally {
      setActingId(null);
    }
  };

  // Booking approval handlers (RPC via stripeRpc.js)
  const approveBooking = async (bookingId) => {
    try {
      setActingId(bookingId);
      setBookings((arr) =>
        arr.map((b) =>
          b.id === bookingId
            ? {
                ...b,
                approvalStatus: "approved",
                status: "paid",
                approvalExpiresAt: null,
              }
            : b
        )
      );
      await bookingApprove(bookingId);
      setMsg(
        "Approved and captured. Confirmation email will follow after payment finalizes."
      );
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Approve failed.");
    } finally {
      setActingId(null);
    }
  };

  const declineBooking = async (bookingId) => {
    try {
      if (
        !window.confirm("Decline this booking? The guest will be notified.")
      )
        return;
      setActingId(bookingId);
      setBookings((arr) =>
        arr.map((b) =>
          b.id === bookingId
            ? { ...b, approvalStatus: "denied", status: "canceled" }
            : b
        )
      );
      await bookingDecline(bookingId);
      setMsg("Declined. Hold released and email queued.");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Decline failed.");
    } finally {
      setActingId(null);
    }
  };

  // ✅ Host-cancel handler for future bookings (backend enforces rules)
  const cancelBookingByHost = async (bookingId, bookingObj) => {
    try {
      if (!callHostCancel)
        throw new Error("Cancel action unavailable (function not deployed).");

      const start = parseYMD(bookingObj?.startDate || bookingObj?.date || "");
      const whenLabel = start ? dayFmt.format(start) : "this booking";

      const startMs = start ? start.getTime() : 0;
      const msToStart = startMs - now;
      const hoursToStart = msToStart > 0 ? msToStart / (1000 * 60 * 60) : 0;
      const isLateWindow = hoursToStart > 0 && hoursToStart <= 48;

      const baseMsg = isLateWindow
        ? `This booking starts soon (${whenLabel}). This will be recorded as a last-minute host cancellation and may affect your reliability.\n\nCancel anyway?`
        : `Cancel ${whenLabel}? The guest will be notified and any eligible refund will be processed.`;

      if (!window.confirm(baseMsg)) return;

      const reasonNote =
        window.prompt(
          "Optional: Why are you canceling? (This helps our support team and trust metrics.)",
          ""
        ) || "";

      const reasonCode = isLateWindow
        ? "host_late_cancel"
        : "host_cancel";

      setActingId(bookingId);

      // Optimistic: mark as canceled while we wait
      setBookings((arr) =>
        arr.map((b) =>
          b.id === bookingId
            ? {
                ...b,
                status: "canceled",
              }
            : b
        )
      );

      const res = await callHostCancel({ bookingId, reasonCode, reasonNote });
      const data = res?.data || {};

      if (!data.ok) {
        setMsg(
          "Cancel failed. It may have started, ended, or changed. Refresh and try again."
        );
        // Optional: we could refetch bookings here; for now, rely on snapshot to bring truth.
      } else {
        const finalStatus = data.status || "canceled";
        setBookings((arr) =>
          arr.map((b) =>
            b.id === bookingId
              ? {
                  ...b,
                  status: finalStatus,
                }
              : b
          )
        );
        setMsg(
          finalStatus === "refunded"
            ? "Booking canceled and fully refunded to the guest."
            : "Booking canceled. Refund (if any) is processing."
        );
      }
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Cancel failed.");
    } finally {
      setActingId(null);
    }
  };

  /* ------------------------------ UI -------------------------------- */
  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold">Bookings</h1>
        <p className="mt-2 text-neutral-600">
          Manage requests, confirmations, and cancellations.
        </p>
        <div className="mt-6 grid gap-4">
          <div className="h-28 animate-pulse rounded-2xl border border-neutral-200 bg-neutral-100" />
          <div className="h-64 animate-pulse rounded-2xl border border-neutral-200 bg-neutral-100" />
        </div>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div>
      <h1 className="text-2xl font-semibold">Bookings</h1>
      <p className="mt-2 text-neutral-600">
        Manage requests, confirmations, and cancellations.
      </p>

      {!!msg && (
        <div className="mt-3 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-800">
          {msg}
        </div>
      )}

      {/* Top chips */}
      <div className="mt-4 flex flex-wrap gap-2 text-xs text-neutral-600">
        <span className="rounded-full bg-neutral-100 px-2 py-1">
          Pending requests: {pendingCount}
        </span>
        <span className="rounded-full bg-neutral-100 px-2 py-1">
          Paid: {counts.paid}
        </span>
        <span className="rounded-full bg-neutral-100 px-2 py-1">
          Pending: {counts.pending}
        </span>
        <span className="rounded-full bg-neutral-100 px-2 py-1">
          Canceled: {counts.canceled}
        </span>
        <span className="rounded-full bg-neutral-100 px-2 py-1">
          Refunded: {counts.refunded}
        </span>
      </div>

      {/* --------------------- Pending Requests --------------------- */}
      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/50">
        <div className="flex items-center justify-between border-b border-amber-200 px-4 py-3">
          <div className="text-sm font-medium text-amber-800">
            Pending requests {pendingCount ? `(${pendingCount})` : ""}
          </div>
          <div className="text-xs text-amber-700">
            Accept or deny before the countdown hits zero.
          </div>
        </div>

        {pendingCount === 0 ? (
          <div className="px-4 py-6 text-sm text-amber-800">
            No pending requests right now.
          </div>
        ) : (
          <ul className="divide-y divide-amber-200">
            {requests.map((r) => {
              const listing = listingsById[r.listingId];
              const guest = guestsById[r.guestUid];
              const span = describeSpan({
                mode: r.mode || "day",
                startDate: r.startDate,
                endDateExclusive: r.endDateExclusive,
              });

              const expiresAtMs = deriveExpiresMs(r, "request");
              const timeLeft = Math.max(0, expiresAtMs - now);
              const urgent = timeLeft > 0 && timeLeft <= 5 * 60 * 1000;

              const guestEmail =
                guest?.email ||
                r?.guestEmail ||
                (r?.participantEmails &&
                  r.participantEmails[r.guestUid]) ||
                (r?.participants && r.participants[r.guestUid]?.email) ||
                null;

              return (
                <li
                  key={r.id}
                  className="grid gap-2 p-4 sm:grid-cols-[1fr,1fr,1fr,auto] sm:items-center"
                >
                  <div>
                    <div className="font-medium text-neutral-900">{span}</div>
                    <div className="text-xs text-neutral-500">
                      {r.mode === "overnight" ? "Overnight" : "Day"} · Requested{" "}
                      {r.createdAt?.toDate
                        ? dayFmt.format(r.createdAt.toDate())
                        : ""}
                    </div>
                  </div>

                  <div>
                    {listing ? (
                      <>
                        <div className="truncate font-medium">
                          {listing.title || "Untitled space"}
                        </div>
                        <div className="truncate text-xs text-neutral-500">
                          {[listing.city, listing.stateCode || listing.state]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      </>
                    ) : (
                      <div className="text-neutral-500">—</div>
                    )}
                  </div>

                  <div>
                    {guest ? (
                      <>
                        <div className="truncate font-medium">
                          {guest.displayName || guest.name || "Guest"}
                        </div>
                        <div className="truncate text-xs text-neutral-500">
                          {guestEmail || guest.uid}
                        </div>
                      </>
                    ) : (
                      <div className="text-neutral-500">
                        {r.guestUid || "—"}
                      </div>
                    )}
                    <div
                      className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs ${
                        timeLeft === 0
                          ? "bg-neutral-200 text-neutral-700"
                          : urgent
                          ? "bg-red-100 text-red-700"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {fmtCountdown(timeLeft)}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-start gap-2 sm:justify-end">
                    <button
                      onClick={() => contactGuest(r, { kind: "request" })}
                      className="rounded-full border border-neutral-300 px-3 py-1 text-sm hover:bg-neutral-50"
                      title="Open message thread"
                    >
                      Contact guest
                    </button>
                    <button
                      onClick={() => handleDeny(r.id)}
                      disabled={actingId === r.id || timeLeft === 0 || !callDeny}
                      className={`rounded-full border px-3 py-1 text-sm ${
                        timeLeft === 0 || !callDeny
                          ? "cursor-not-allowed border-neutral-200 text-neutral-400"
                          : "border-neutral-300 hover:bg-neutral-50"
                      }`}
                    >
                      {actingId === r.id ? "…" : "Deny"}
                    </button>
                    <button
                      onClick={() => handleAccept(r.id)}
                      disabled={actingId === r.id || timeLeft === 0 || !callAccept}
                      className={`rounded-full px-3 py-1 text-sm text-white ${
                        timeLeft === 0 || !callAccept
                          ? "cursor-not-allowed bg-neutral-300"
                          : "bg-emerald-600 hover:bg-emerald-700"
                      }`}
                    >
                      {actingId === r.id ? "…" : "Accept"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* --------------------- Confirmed / In-Review Bookings --------------------- */}
      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 text-left">
            <tr>
              <th className="px-4 py-2">Date / Span</th>
              <th className="px-4 py-2">Listing</th>
              <th className="px-4 py-2">Guest</th>
              <th className="px-4 py-2">Total</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {emptyBookings ? (
              <tr>
                <td className="px-4 py-3 text-neutral-500" colSpan={6}>
                  No bookings yet.
                </td>
              </tr>
            ) : (
              bookings.map((b) => {
                const listing = listingsById[b.listingId];
                const guest = guestsById[b.guestUid];
                const span = describeSpan({
                  mode: b.mode || "day",
                  startDate: b.startDate,
                  endDateExclusive: b.endDateExclusive,
                });

                const status = String(b.status || "pending").toLowerCase();
                const badge =
                  status === "paid"
                    ? "bg-green-100 text-green-700 border-green-200"
                    : status === "refunded"
                    ? "bg-blue-100 text-blue-700 border-blue-200"
                    : status === "canceled"
                    ? "bg-neutral-100 text-neutral-700 border-neutral-200"
                    : "bg-amber-100 text-amber-700 border-amber-200";

                // Show decision chip ONLY when truly pending + with a valid expiry
                const isApprovalPending =
                  String(b.approvalStatus || "").toLowerCase() ===
                    "pending" ||
                  String(b.status || "").toLowerCase() === "authorized" ||
                  String(b.status || "").toLowerCase() === "awaiting_host";

                let decisionExpiresMs = toMs(pickExpiresField(b));
                if (!decisionExpiresMs && isApprovalPending) {
                  decisionExpiresMs = deriveExpiresMs(b, "booking");
                }

                const decisionTimeLeft = Math.max(
                  0,
                  decisionExpiresMs ? decisionExpiresMs - now : 0
                );
                const decisionUrgent =
                  decisionTimeLeft > 0 &&
                  decisionTimeLeft <= 5 * 60 * 1000;
                const decisionExpired = decisionExpiresMs
                  ? decisionTimeLeft === 0
                  : false;
                const showDecision = isApprovalPending && !!decisionExpiresMs;

                // ✅ Host-cancel eligibility for future bookings (backend still hard-enforces)
                const startForCancel = parseYMD(b.startDate || b.date || "");
                const canHostCancel =
                  (status === "paid" ||
                    status === "pending" ||
                    status === "authorized") &&
                  !!startForCancel &&
                  startForCancel.getTime() > now &&
                  !showDecision;

                const guestEmail =
                  guest?.email ||
                  b?.guestEmail ||
                  (b?.participantEmails &&
                    b.participantEmails[b.guestUid]) ||
                  (b?.participants &&
                    b.participants[b.guestUid]?.email) ||
                  null;

                return (
                  <tr key={b.id} className="border-t border-neutral-200">
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium">{span}</div>
                      <div className="text-xs text-neutral-500">
                        {b.mode === "overnight" ? "Overnight" : "Day"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {listing ? (
                        <>
                          <div className="truncate font-medium">
                            {listing.title || "Untitled space"}
                          </div>
                          <div className="truncate text-xs text-neutral-500">
                            {[listing.city, listing.stateCode || listing.state]
                              .filter(Boolean)
                              .join(", ")}
                          </div>
                        </>
                      ) : (
                        <div className="text-neutral-500">—</div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {guest ? (
                        <>
                          <div className="truncate font-medium">
                            {guest.displayName || guest.name || "Guest"}
                          </div>
                          <div className="truncate text-xs text-neutral-500">
                            {guestEmail || guest.uid}
                          </div>
                        </>
                      ) : (
                        <div className="text-neutral-500">
                          {b.guestUid || "—"}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {fmtUSD(b.amountTotalCents)}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex w-fit rounded-full border px-2 py-0.5 text-xs ${badge}`}
                        >
                          {status[0].toUpperCase() + status.slice(1)}
                          {b.paymentRoute === "platform_fallback"
                            ? " · platform hold"
                            : ""}
                        </span>

                        {showDecision && (
                          <span
                            className={`inline-flex w-fit rounded-full px-2 py-0.5 text-xs ${
                              decisionExpired
                                ? "bg-neutral-200 text-neutral-700"
                                : decisionUrgent
                                ? "bg-red-100 text-red-700"
                                : "bg-amber-100 text-amber-800"
                            }`}
                            title="Approval decision window"
                          >
                            {fmtCountdown(decisionTimeLeft)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-wrap gap-2">
                        {/* Approve/Deny for manual-capture bookings */}
                        {showDecision && (
                          <>
                            <button
                              onClick={() => declineBooking(b.id)}
                              disabled={actingId === b.id || decisionExpired}
                              className={`rounded-full border px-3 py-1 text-sm ${
                                decisionExpired
                                  ? "cursor-not-allowed border-neutral-200 text-neutral-400"
                                  : "border-neutral-300 hover:bg-neutral-50"
                              }`}
                              title={
                                decisionExpired
                                  ? "Decision window expired"
                                  : "Decline booking"
                              }
                            >
                              {actingId === b.id ? "…" : "Decline"}
                            </button>
                            <button
                              onClick={() => approveBooking(b.id)}
                              disabled={actingId === b.id || decisionExpired}
                              className={`rounded-full px-3 py-1 text-sm text-white ${
                                decisionExpired
                                  ? "cursor-not-allowed bg-neutral-300"
                                  : "bg-emerald-600 hover:bg-emerald-700"
                              }`}
                              title={
                                decisionExpired
                                  ? "Decision window expired"
                                  : "Approve & capture"
                              }
                            >
                              {actingId === b.id ? "…" : "Approve"}
                            </button>
                          </>
                        )}

                        {/* ✅ Host cancel (future bookings, backend enforces window & refunds) */}
                        {canHostCancel && (
                          <button
                            onClick={() => cancelBookingByHost(b.id, b)}
                            disabled={actingId === b.id || !callHostCancel}
                            className={`rounded-full border px-3 py-1 text-sm ${
                              !callHostCancel
                                ? "cursor-not-allowed border-neutral-200 text-neutral-400"
                                : "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                            }`}
                            title={
                              !callHostCancel
                                ? "Cancel function not deployed"
                                : "Cancel booking"
                            }
                          >
                            {actingId === b.id ? "Canceling…" : "Cancel booking"}
                          </button>
                        )}

                        <button
                          onClick={() => contactGuest(b)}
                          className="rounded-full border border-neutral-300 px-3 py-1 hover:bg-neutral-50"
                          title="Open message thread"
                        >
                          Contact guest
                        </button>

                        <Link
                          to={`/listing/${encodeURIComponent(b.listingId)}`}
                          state={listing ? { listing } : undefined}
                          className="rounded-full border border-neutral-300 px-3 py-1 hover:bg-neutral-50"
                        >
                          View listing
                        </Link>

                        {b.stripeReceiptUrl && status === "paid" && (
                          <a
                            href={b.stripeReceiptUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-full border border-neutral-300 px-3 py-1 hover:bg-neutral-50"
                            title="Receipt"
                          >
                            Receipt
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-neutral-600">
        Tip: open a booking or request and use{" "}
        <span className="font-medium">Contact guest</span> to jump into its
        chat thread, or view all conversations from{" "}
        <Link to="/messages" className="underline">
          Messages
        </Link>
        .
      </div>
    </div>
  );
}
